import { EmptyState } from '../Kit/_index';
import { onBuildTicks, onFormatShort, onNiceMax } from './_chartScale';

const BLOCK = 'LStudio__app_chart';

const VIEW_W = 600;
const VIEW_H = 220;
const PAD_TOP = 8;

/*
    🔥 ÁREA APILADA

    series: [{ label, tone, values: [] }]

    ⚠️ APILADA NO ES LO MISMO QUE SUPERPUESTA

    Cada serie se dibuja SOBRE la suma de las anteriores, no desde
    cero. Por eso el techo del eje es el máximo de las SUMAS, no el de
    la serie más alta: si fuera el segundo, la parte de arriba se
    saldría del gráfico.

    ⚠️ SE DIBUJA DE LA ÚLTIMA A LA PRIMERA

    En SVG lo que se pinta después queda encima. Recorriendo al revés,
    la primera serie —la de abajo, la más importante— queda visible
    entera en lugar de tapada por las que se apilan arriba.
*/

export const ChartArea = ({
    series = [],  labels = [],   max = null,
    height = 220,  showGrid = true,   showLegend = true,
    formatValue = onFormatShort,  emptyLabel = 'Sin datos para graficar',
    ariaLabel = 'Gráfico de área apilada',
}) => {

    const HAS_DATA = series.some((s) => (s.values ?? []).length);

    if (!HAS_DATA) return <EmptyState title={emptyLabel} size="sm" />;

    const LENGTH = Math.max(...series.map((s) => (s.values ?? []).length));

    // 🔥 El techo sale de las SUMAS por punto, no de la serie más alta
    const SUMS = Array.from({ length: LENGTH }, (_, i) => (
        series.reduce((acc, s) => acc + (Number(s.values?.[i]) || 0), 0)
    ));

    const MAX = max ?? onNiceMax(Math.max(0, ...SUMS));

    const TICKS = onBuildTicks(MAX, 4);

    const USABLE = VIEW_H - PAD_TOP;

    const onX = (i) => (LENGTH === 1 ? VIEW_W / 2 : (i / (LENGTH - 1)) * VIEW_W);

    const onY = (value) => PAD_TOP + USABLE - (value / MAX) * USABLE;

    // 🔥 Acumulado: cada serie arranca donde termina la anterior
    const STACKS = [];

    let running = new Array(LENGTH).fill(0);

    series.forEach((serie) => {

        const BOTTOM = [...running];

        const TOP = running.map((base, i) => base + (Number(serie.values?.[i]) || 0));

        STACKS.push({ serie, bottom: BOTTOM, top: TOP });

        running = TOP;
    });

    return (
        <div className={BLOCK} style={{ '--LS__chart_h': `${height}px` }}>

            {
                showLegend && series.length > 1 && (
                    <div className={`${BLOCK}__legend`}>
                        {
                            series.map((s, i) => (
                                <span key={s.label ?? i} className={`${BLOCK}__legend_item`}
                                    data-tone={s.tone ?? 'accent'} >
                                    <i className={`${BLOCK}__legend_dot`} aria-hidden="true" />
                                    {s.label}
                                </span>
                            ))
                        }
                    </div>
                )
            }

            <div className={`${BLOCK}__plot`}>

                {
                    showGrid && (
                        <div className={`${BLOCK}__axis`} aria-hidden="true">
                            {
                                [...TICKS].reverse().map((tick) => (
                                    <span key={tick} className={`${BLOCK}__axis_tick`}>
                                        {formatValue(tick)}
                                    </span>
                                ))
                            }
                        </div>
                    )
                }

                <svg className={`${BLOCK}__svg`} viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
                    preserveAspectRatio="none" role="img" aria-label={ariaLabel} >

                    {
                        showGrid && TICKS.map((tick) => (
                            <line key={tick} className={`${BLOCK}__grid`}
                                x1="0" x2={VIEW_W} y1={onY(tick)} y2={onY(tick)} />
                        ))
                    }

                    {/* ⚠️ Al revés: la primera serie queda encima, no tapada */}
                    {
                        [...STACKS].reverse().map(({ serie, bottom, top }, index) => {

                            const UP = top.map((v, i) => `${i === 0 ? 'M' : 'L'}${onX(i)},${onY(v)}`).join(' ');

                            const DOWN = [...bottom].reverse().map((v, i) => `L${onX(LENGTH - 1 - i)},${onY(v)}`).join(' ');

                            return (
                                <g key={serie.label ?? index} data-tone={serie.tone ?? 'accent'}>

                                    <path className={`${BLOCK}__stack`} d={`${UP} ${DOWN} Z`} />

                                    <path className={`${BLOCK}__line`} d={UP} vectorEffect="non-scaling-stroke" />

                                </g>
                            );
                        })
                    }

                </svg>

            </div>

            {
                labels.length > 0 && (
                    <div className={`${BLOCK}__labels`} aria-hidden="true">
                        {labels.map((l, i) => <span key={i} className={`${BLOCK}__label`}>{l}</span>)}
                    </div>
                )
            }

            <table className={`${BLOCK}__sr`}>
                <caption>{ariaLabel}</caption>
                <tbody>
                    {
                        series.map((s, i) => (
                            <tr key={s.label ?? i}>
                                <th scope="row">{s.label}</th>
                                <td>{(s.values ?? []).map(formatValue).join(', ')}</td>
                            </tr>
                        ))
                    }
                </tbody>
            </table>

        </div>
    );
};
